import { CircularProgress, makeStyles, Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import cn from 'classnames';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';
import _pick from 'lodash/pick';
import {
  getAllCustomQuestions,
  attachFileToCustomQuestion,
} from '../api/CustomQuestion';
import { Audience } from '../api/CustomQuestion/types/Audience';
import { CustomQuestion } from '../api/CustomQuestion/types/CustomQuestion';
import { CustomQuestionType } from '../api/CustomQuestion/types/Type';
import authAPI, { ConsentType, IMentorRegistrationForm } from '../api/auth';
import { EnrichedMentorResponse } from '../api/enrich';
import mentorsAPI from '../api/mentors';
import specializationsAPI, { Specialization } from '../api/specializations';
import { getSettings } from '../api/tenants';
import { TenantId } from '../api/tenants/Tenant';
import { Settings } from '../api/tenants/types/Settings';
import { SnackMessage, Text } from '../components/common';
import { Attachment } from '../components/common/attachment-card';
import { FormValues } from '../components/common/wizard';
import MentorsApplicationsWizardForm from '../components/forms/report-mentors-applications-wizard';
import { useResourceBundles } from '../contexts/resource-bundles-context';
import { COLORS } from '../theme/variables';
import {
  createAttachmentMetadata,
  extractFilePathFromResponse,
  getContentTypeFromExtension,
  handleSubmitCustomForm,
} from '../utils/custom-questions';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    height: '100vh',
    overflow: 'auto',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: 0,

    [theme.breakpoints.up('xs')]: {
      width: 'calc(100% - 60px)',
      padding: '50px 30px',
    },

    [theme.breakpoints.up('md')]: {
      width: '100%',
      padding: '50px 0',
    },
  },
  block: {
    width: '700px',
    padding: '32px',
    background: '#FFFFFF',
    boxShadow:
      '0px 0px 2px rgba(34, 91, 187, 0.16), 0px 4px 8px rgba(51, 126, 255, 0.04), 0px 8px 16px rgba(51, 126, 255, 0.04)',
    borderRadius: 0,
    boxSizing: 'border-box',

    [theme.breakpoints.up('xs')]: {
      borderRadius: '16px',
    },
  },
  loadingBlock: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingContainer: {
    height: '100vh',
    padding: 0,

    [theme.breakpoints.up('xs')]: {
      width: '100%',
    },
  },
  successBlock: {
    width: 300,
    minHeight: 180,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'start',
    borderRadius: '16px',
  },
  successIcon: {
    fontSize: 30,
    color: COLORS.COLOR_GREEN_BASE,
    marginBottom: 15,
  },
  successText: {
    textAlign: 'center',
  },
  title: {
    marginBottom: 15,
  },
}));

function parseFilterFromURL(search: string): any {
  return _pick(parseQuery(search), ['key']);
}

const formatValuesRequest = (
  values: FormValues,
  tenantId: string,
): IMentorRegistrationForm => {
  return {
    mentor: {
      firstName: values.firstName || '',
      lastName: values.lastName || '',
      about: values.about || '',
      dateOfBirth: values.dateOfBirth || null,
      linkedInProfile: values.linkedInProfile || '',
      country: values.country || '',
      state: values.state || '',
      city: values.city || '',
      address: values.address || '',
      zip: values.zip || '',
      email: values.email || '',
      phone: values.phone || '',
      tenantId: tenantId || '',
      mentorAdditionalInfo: {
        tenantId: tenantId || '',
        gender: values.gender || '',
        race: values.race || '',
        veteran: values.veteran === 'Yes',
        whyBecomeMentor: values.whyBecomeMentor || '',
        salesChallenges: values.salesChallenges || '',
        marketingChallenges: values.marketingChallenges || '',
        fundraisingChallenges: values.fundraisingChallenges || '',
        businessChallenges: values.businessChallenges || '',
      },
      currentRole: values.currentRole || '',
      companyName: values.companyName || '',
      seniority: values.seniority || '',
      experienceYears:
        values.experienceYears !== '' && values.experienceYears != null
          ? Number(values.experienceYears)
          : null,
      isEnterpreneur: values.isEnterpreneur === 'true',
      isInvestor: values.isInvestor === 'true',
      mentorCapacity:
        values.mentorCapacity !== '' && values.mentorCapacity != null
          ? Number(values.mentorCapacity)
          : null,
      skills: values.skills || '',
    },
    mentorSpecializationList: values.businessIndustries
      ? // @ts-expect-error
        values.businessIndustries.map((id) => ({ specializationId: id }))
      : [],
  };
};

function mapMentorEnrichToFormValues(
  data: EnrichedMentorResponse,
  specializations?: Specialization[],
): Partial<FormValues> {
  const directFields = [
    'firstName', 'lastName', 'phone', 'about', 'linkedInProfile',
    'currentRole', 'companyName', 'seniority', 'skills',
    'address', 'city', 'state', 'zip', 'country',
  ] as const;

  const values: Partial<FormValues> = {};

  for (const key of directFields) {
    if (data[key]) values[key] = data[key];
  }

  // Special cases: dual mapping, type conversion, nested source
  if (data.email) {
    values.email = data.email;
    values.emailConfirm = data.email;
  }
  if (data.dateOfBirth) {
    values.dateOfBirth = data.dateOfBirth;
  }
  if (data.experienceYears != null) {
    values.experienceYears = String(data.experienceYears);
  }
  if (data.mentorCapacity != null) {
    values.mentorCapacity = String(data.mentorCapacity);
  }
  if (data.isEnterpreneur != null) {
    values.isEnterpreneur = String(data.isEnterpreneur);
  }
  if (data.isInvestor != null) {
    values.isInvestor = String(data.isInvestor);
  }
  if (data.mentorAdditionalInfo?.gender) {
    values.gender = data.mentorAdditionalInfo.gender;
  }
  if (data.mentorAdditionalInfo?.race) {
    values.race = data.mentorAdditionalInfo.race;
  }
  if (data.mentorAdditionalInfo?.whyBecomeMentor) {
    values.whyBecomeMentor = data.mentorAdditionalInfo.whyBecomeMentor;
  }
  if (data.mentorAdditionalInfo?.businessChallenges) {
    values.businessChallenges = data.mentorAdditionalInfo.businessChallenges;
  }

  // Match specialization names to IDs
  if (data.specializations?.length && specializations?.length) {
    const nameToId = new Map<string, string>();
    for (const s of specializations) {
      nameToId.set(s.name.toLowerCase(), s.id);
    }
    const matchedIds = data.specializations
      .map((name) => nameToId.get(name.toLowerCase()))
      .filter((id): id is string => id != null);
    if (matchedIds.length) {
      (values as any).businessIndustries = matchedIds;
    }
  }

  return values;
}

function MentorsApplicationsWizardPage() {
  const classes = useStyles();
  const location = useLocation();
  const { rb } = useResourceBundles();

  const [statusForm, setStatusForm] = useState<
    'success' | 'loading' | 'error' | ''
  >('loading');
  const [consents, setConsents] = useState<ConsentType[]>([]);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [logoBlob, setLogoBlob] = useState<string>('');
  const [additionalInfoCustomQuestions, setAdditionalInfoCustomQuestions] =
    useState<CustomQuestion[] | null>(null);
  const [isLoadingSaveForm, setIsLoadingSaveForm] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [uploadLogoError, setUploadLogoError] = useState(false);
  const [attachments, setAttachments] = useState<Map<string, Attachment>>(
    new Map(),
  );
  const [files, setFiles] = useState<Map<string, File>>(new Map());
  const [showPrefillModal, setShowPrefillModal] = useState(true);
  const [prefillData, setPrefillData] = useState<Partial<FormValues> | null>(
    null,
  );

  const { key } = useMemo(
    () => parseFilterFromURL(location.search),
    [location.search],
  );

  const getParseTenantId = (value: string) => {
    const parseData = value.split('|');
    return parseData[0];
  };

  const tenantId = useMemo(() => {
    return getParseTenantId(atob(key)) || '';
  }, [key]);

  const handlePrefillComplete = useCallback(
    async (data: EnrichedMentorResponse) => {
      let specializations: Specialization[] = [];
      if (data.specializations?.length) {
        try {
          specializations = await specializationsAPI.getPublicAll(
            tenantId as TenantId,
          );
        } catch (_e) {
          // proceed without specialization matching
        }
      }
      const mapped = mapMentorEnrichToFormValues(data, specializations);
      setPrefillData(mapped);
      setShowPrefillModal(false);
    },
    [tenantId],
  );

  const handlePrefillDismiss = useCallback(() => {
    setShowPrefillModal(false);
  }, []);

  const loadData = async () => {
    try {
      const [loadedConsents, settings] = await Promise.all([
        authAPI.getMentorConsents(tenantId),
        getSettings(tenantId as TenantId),
      ]);
      if (settings?.customQuestionsMentorAdditionalInfoEnabled) {
        const customQuestions = await getAllCustomQuestions(
          tenantId as TenantId,
          Audience.MENTOR_ADDITIONAL_INFO,
        );
        setAdditionalInfoCustomQuestions(customQuestions);
      }
      setSettings(settings);
      setConsents(loadedConsents);
      setStatusForm('');
    } catch (e) {
      setStatusForm('error');
    }
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsLoadingSaveForm(true);
      setUploadLogoError(false);
      const file = (values.file as unknown as File) || undefined;
      const validValue = formatValuesRequest(values, tenantId);
      const mentor = await authAPI.mentorRegistration(validValue);

      if (file) {
        try {
          await mentorsAPI.updateAvatarPublic(
            tenantId as TenantId,
            mentor.id,
            file,
          );
        } catch (error) {
          setUploadLogoError(true);
        }
      }

      if (
        settings?.customQuestionsMentorAdditionalInfoEnabled &&
        additionalInfoCustomQuestions?.length
      ) {
        // Upload attachment files first
        const updatedValues = { ...values };
        const updatedAttachments = new Map(attachments);

        // Process all attachment questions
        for (const question of additionalInfoCustomQuestions) {
          if (question.type === CustomQuestionType.ATTACHMENT) {
            const questionId = question.id;
            const currentValue = values[questionId];

            // Check if file needs to be uploaded (has pending marker or file in Map)
            const file = files.get(questionId);
            const hasPendingMarker =
              typeof currentValue === 'string' &&
              currentValue.startsWith('pending:');

            // Check if currentValue is already a valid JSON string (from previous save)
            const isAlreadyJsonString =
              typeof currentValue === 'string' &&
              currentValue.trim() !== '' &&
              !hasPendingMarker &&
              (currentValue.startsWith('{') || currentValue.startsWith('"'));

            if (file && hasPendingMarker) {
              try {
                // Upload file to server
                const response = await attachFileToCustomQuestion(
                  tenantId as TenantId,
                  questionId,
                  mentor.id,
                  file,
                );

                const filePath = extractFilePathFromResponse(response);

                if (!filePath) {
                  throw new Error('Failed to get file path from response');
                }

                // Update attachment with URL
                const attachment = attachments.get(questionId);
                if (attachment) {
                  // Clean up temporary blob URL if exists
                  if (attachment.url && attachment.url.startsWith('blob:')) {
                    URL.revokeObjectURL(attachment.url);
                  }

                  const updatedAttachment = {
                    ...attachment,
                    url: filePath,
                  };
                  updatedAttachments.set(questionId, updatedAttachment);

                  // Create AttachmentMetadata object for backend
                  updatedValues[questionId] = createAttachmentMetadata(
                    filePath,
                    `${attachment.name}.${attachment.extension}`,
                    attachment.size,
                    getContentTypeFromExtension(attachment.extension),
                  );
                }
              } catch (error: any) {
                console.error('Error uploading attachment:', error);
                enqueueSnackbar(
                  `Failed to upload attachment for question: ${question.label}`,
                  {
                    variant: 'error',
                  },
                );
                // Remove pending marker on error
                updatedValues[questionId] = '';
              }
            } else if (isAlreadyJsonString) {
              // Keep existing JSON string value (from previous save)
              updatedValues[questionId] = currentValue;
            } else if (
              !currentValue ||
              currentValue === '' ||
              hasPendingMarker
            ) {
              // No file selected or pending marker without file - clear value
              updatedValues[questionId] = '';
            }
          }
        }

        await handleSubmitCustomForm({
          values: updatedValues,
          questions: additionalInfoCustomQuestions,
          responderId: mentor.id,
          tenantId: tenantId as TenantId,
          audience: Audience.MENTOR_ADDITIONAL_INFO,
        });
      }
      setStatusForm('success');
      setIsLoadingSaveForm(false);
    } catch (e: any) {
      setIsLoadingSaveForm(false);
      const messageError = e.response?.data?.message || 'Internal server error';

      enqueueSnackbar('An error occurred while enrollment', {
        variant: 'error',
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (statusForm === 'loading') {
    return (
      <div
        data-testid='report-page'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <div className={cn(classes.block, classes.loadingBlock)}>
          <CircularProgress size={54} color='primary' />
        </div>
      </div>
    );
  }

  if (statusForm === 'success') {
    return (
      <div
        data-testid='mentor-wizard-page-success'
        className={cn(classes.container, classes.loadingContainer)}
      >
        <div className={cn(classes.block, classes.successBlock)}>
          <CheckCircleIcon className={classes.successIcon} />
          <div className={classes.successText}>
            <Text variant='normal'>
              {uploadLogoError
                ? 'We received your application, thank you. There was a hiccup with the photo upload, but this does not affect your application status, program admin will reach out to you shortly.'
                : 'Success! Your application has been submitted, and a copy of it has been sent to your email address.'}
            </Text>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id='wizard-scroll' className={classes.wrapper}>
      <div data-testid='report-page' className={classes.container}>
        <div className={classes.block}>
          <Typography variant='h2' className={classes.title}>
            New {rb('mentor-u')} Application Form
          </Typography>
          <MentorsApplicationsWizardForm
            onSubmit={handleSubmit}
            tenantId={tenantId}
            isLoading={isLoadingSaveForm}
            consents={consents}
            showCustomAdditionalInfo={
              !!settings?.customQuestionsMentorAdditionalInfoEnabled
            }
            additionalInfoCustomQuestions={additionalInfoCustomQuestions}
            intro={settings?.mentorApplicationIntro || ''}
            logoBlob={logoBlob}
            setLogoBlob={setLogoBlob}
            attachments={attachments}
            setAttachments={setAttachments}
            files={files}
            setFiles={setFiles}
            mentorCommitmentEnabled={settings?.mentorCommitmentEnabled ?? false}
            showPrefillModal={showPrefillModal}
            onPrefillComplete={handlePrefillComplete}
            onPrefillDismiss={handlePrefillDismiss}
            prefillData={prefillData}
          />
        </div>
      </div>
    </div>
  );
}

export default MentorsApplicationsWizardPage;
